'use client';

import { ErrorBoundary } from '@sentry/nextjs';
import { useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
import { useMemo, useState } from 'react';

import { AlarmAdd, CheckOutlined, Close, Eye, Versus } from '@zoomit/icons';
import { Sheet } from '@zoomit/mobile-sheet';
import {
  Box,
  Flex,
  IconButton,
  Portal,
  Skeleton,
  Spacing,
  cn,
  localeIRNumberFormatter,
} from '@zoomit/primitives';
import { Button, Hr, Typography, cssVar, theme } from '@zoomit/zoomit-ds';

import { getProductDetails } from '../../apis/product';
import { OriginURL } from '../../constants/apis';
import { singleProductTabButtons } from '../../constants/product';
import { useComparedProductsStore } from '../../hooks/useComparedProductsStore';
import { usePriceNotify } from '../../hooks/usePriceNotify';
import type { ProductListItemType, SingleProductTabButtons } from '../../types/product';
import { SomethingWentWrong } from '../empty-state';
import {
  PhysicalStoreListItemMobile,
  PriceNotifyModal,
  ProductMediaPreviewMobile,
  ProductStoreListItemMobile,
} from '../product';
import { KeySpecs, Price, Score } from '../shared';

type Props = {
  isOpen: boolean;
  hasCompare?: boolean;
  productInfo: ProductListItemType;
  handleClose: () => void;
  storesId?: string;
  specificationsId?: string;
};

const ProductInfoSheet = (props: Props) => {
  const { productInfo, isOpen, handleClose, hasCompare = true, storesId, specificationsId } = props;
  const { addProduct, productsList, removeProduct } = useComparedProductsStore();
  const [activeTab, setActiveTab] = useState<SingleProductTabButtons['value']>('online-shops');
  const router = useRouter();

  const {
    data: additionalData,
    isFetching: isAdditionalDataFetching,
    isFetched: isAdditionalDataFetched,
    isSuccess: isAdditionalDataSuccess,
  } = useQuery(
    ['getProductAdditionalData', `productId:${productInfo?.id}`],
    async () => {
      const res = await getProductDetails({ id: productInfo!.id });
      return res.data;
    },
    {
      enabled: !!productInfo,
      keepPreviousData: true,
    },
  );

  const { priceNotifyPopOverState, notifyQuery, deleteMutation, postMutation } = usePriceNotify({
    productId: productInfo?.id,
  });

  const ImageGallery = useMemo(() => {
    if (isAdditionalDataFetching) {
      return (
        <Flex flexDirection="column" alignItems="center" flexWrap="nowrap">
          <div className="relative aspect-[3/2] w-full">
            <Skeleton variant="rectangular" width="90%" height="100%" className="mx-auto my-0" />
          </div>
          <Flex justifyContent="center" spacing={{ my: 'x2' }} gap={0.8}>
            {Array.from({ length: 6 }).map((_, index) => (
              <Skeleton variant="text" width={1} key={`skeleton-dot-${index}`} />
            ))}
          </Flex>
        </Flex>
      );
    } else if (isAdditionalDataFetched && isAdditionalDataSuccess) {
      return (
        <Box spacing={{ px: 'x4' }}>
          <ErrorBoundary fallback={<SomethingWentWrong />}>
            <ProductMediaPreviewMobile productId={productInfo.id} galleryItems={additionalData.gallery} />
          </ErrorBoundary>
        </Box>
      );
    }
  }, [productInfo, isAdditionalDataFetching, isAdditionalDataFetched, isAdditionalDataSuccess]);

  const StoresList = useMemo(() => {
    if (isAdditionalDataFetching) {
      return (
        <div className="bg-background1 px-0 py-4">
          <Flex
            className="no-scrollbar w-full overflow-auto"
            gap={Spacing.x4}
            flexWrap="nowrap"
            spacing={{ px: 'x6', pb: 'x2' }}
          >
            {Array.from({ length: 6 }).map((_, index) => (
              <div className="min-w-[270px]" key={`skeleton-store-${index}`}>
                <Skeleton variant="text" width={27} height={17.73} />
              </div>
            ))}
          </Flex>
        </div>
      );
    } else if (isAdditionalDataFetched && isAdditionalDataSuccess && additionalData.stores.length > 0) {
      return (
        <div className="bg-background1 px-0 py-4">
          <Flex className="no-scrollbar w-full overflow-auto" gap={Spacing.x4} flexWrap="nowrap">
            <Flex className="w-full flex-col gap-4">
              <Flex className="mr-4 gap-4">
                {singleProductTabButtons.map((tab) => (
                  <Button
                    onClick={() => {
                      setActiveTab(tab.value);
                    }}
                    className="text-subhead-bold text-nowrap"
                    variant={activeTab === tab.value ? 'contained' : 'outline'}
                    hidden={tab.value === 'physical-shops' && !additionalData?.physicalStores?.length}
                  >
                    {tab.label}
                  </Button>
                ))}
              </Flex>
              {activeTab === 'online-shops' ? (
                <div className="no-scrollbar flex w-full flex-nowrap overflow-scroll">
                  {additionalData?.stores?.map((store, index, all) => (
                    <div
                      className={cn(all.length > 1 ? 'mr-6 min-w-[270px] last:ml-6' : 'w-full')}
                      key={`store-${store.storeId}`}
                    >
                      <ProductStoreListItemMobile
                        positionId={index}
                        key={`product-stores-${store.storeId}`}
                        {...store}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-scrollbar flex w-full flex-nowrap overflow-scroll">
                  {additionalData?.physicalStores?.map((store, index, all) => (
                    <div
                      className={cn(all.length > 1 ? 'mr-6 min-w-[270px] last:ml-6' : 'mx-6 w-full')}
                      key={`store-${store.storeId}`}
                    >
                      <PhysicalStoreListItemMobile {...store} />
                    </div>
                  ))}
                </div>
              )}
            </Flex>
          </Flex>
          {additionalData.otherStoresCount > 0 && (
            <Flex spacing={{ px: 'x6', pt: 'x2' }} gap={0.8}>
              <Typography fontWeight="bold" className="fa">
                {additionalData.otherStoresCount}
              </Typography>
              <Typography textColor={cssVar(theme.grey1)}>فروشنده دیگر نیز این محصول را دارند</Typography>
            </Flex>
          )}
        </div>
      );
    } else {
      return null;
    }
  }, [additionalData, isAdditionalDataFetching, isAdditionalDataFetched, activeTab]);

  if (!isOpen) return null;

  return (
    <Portal>
      <Sheet isOpen={isOpen} onClose={handleClose} disableDrag>
        <Sheet.Container isFullScreen hasBorderRadius={false}>
          <Box
            backgroundColor={cssVar(theme.card)}
            style={{ overflow: 'auto', height: '100vh' }}
            spacing={{ pb: 6.4 }}
          >
            <Flex className="relative h-12" spacing={{ py: 'x4', mx: 'x6' }}>
              <Flex gap={Spacing.x4} alignItems="center" justifyContent="space-between">
                <IconButton
                  onClick={() => {
                    if (notifyQuery.data) {
                      deleteMutation.mutate();
                    } else {
                      priceNotifyPopOverState.open();
                    }
                  }}
                  disabled={notifyQuery.data ? deleteMutation.isLoading : notifyQuery.isLoading}
                >
                  <AlarmAdd fill={notifyQuery.data ? cssVar(theme.success1) : cssVar(theme.grey1)} />
                </IconButton>
                {hasCompare && (
                  <IconButton
                    onClick={() => {
                      if (productsList.has(productInfo)) {
                        removeProduct(productInfo);
                      } else {
                        addProduct(productInfo);
                      }
                    }}
                  >
                    <Versus
                      fill={productsList.has(productInfo) ? cssVar(theme.success1) : cssVar(theme.grey1)}
                    />
                  </IconButton>
                )}
              </Flex>
              <IconButton
                className="absolute top-[calc(50%+2px)] left-0 -translate-1/2"
                onClick={handleClose}
              >
                <Close fill={cssVar(theme.grey3)} />
              </IconButton>
            </Flex>
            <Hr />
            {ImageGallery}
            <Flex flexDirection="column" alignItems="center" gap={1.6} spacing={{ px: 'x6', mt: 'x3' }}>
              <Flex flexDirection="column" style={{ width: '100%' }} gap={0.8}>
                <Typography
                  variant="subhead-bold"
                  fontSize={1.6}
                  textAlign={'right'}
                  lineHeight={2}
                  style={{ width: '100%' }}
                >
                  {productInfo.title}
                </Typography>
                <Flex justifyContent="space-between" alignItems="center" style={{ width: '100%' }}>
                  {productInfo.minPrice ? (
                    <Price price={localeIRNumberFormatter(productInfo.minPrice)} />
                  ) : (
                    <Typography variant="h6" textColor={cssVar(theme.grey1)}>
                      ناموجود
                    </Typography>
                  )}
                  {productInfo.score && <Score score={productInfo.score} />}
                </Flex>
              </Flex>
            </Flex>

            <KeySpecs className="my-4 px-6" data={productInfo.keySpecifications} />

            {StoresList}

            <Flex flexDirection="column" gap={0.8} spacing={{ my: 'x4', px: 'x6' }}>
              {notifyQuery.data ? (
                <Button
                  className="text-callout p-0 [&div]:justify-start"
                  variant="text"
                  size="fit"
                  fontWeight="normal"
                  rightIcon={<CheckOutlined className="!fill-success1" size="sm" />}
                  onClick={() => deleteMutation.mutate()}
                  disabled={deleteMutation.isLoading}
                >
                  <Typography textColor={cssVar(theme.success1)}>اطلاع‌رسانی قیمت</Typography>
                </Button>
              ) : (
                <Button
                  className="text-callout p-0 [&div]:justify-start"
                  variant="text"
                  size="fit"
                  fontWeight="normal"
                  rightIcon={<AlarmAdd className="!fill-grey1" size="sm" />}
                  onClick={priceNotifyPopOverState.open}
                  disabled={notifyQuery.isLoading}
                >
                  اطلاع‌رسانی قیمت
                </Button>
              )}

              {hasCompare &&
                (productsList.has(productInfo) ? (
                  <button
                    className="text-grey1 text-h6 flex items-center gap-2 p-0 leading-2 font-normal"
                    onClick={() => removeProduct(productInfo)}
                    color={cssVar(theme.success1)}
                  >
                    <CheckOutlined fill={cssVar(theme.success1)} size="sm" />
                    <Typography textColor={cssVar(theme.success1)}> به لیست مقایسه اضافه شد</Typography>
                  </button>
                ) : (
                  <button
                    className="text-callout text-h6 flex items-center gap-2 p-0 leading-7 font-normal"
                    onClick={() => addProduct(productInfo)}
                    color={cssVar(theme.grey1)}
                  >
                    <Versus fill={cssVar(theme.grey1)} size="sm" />
                    افزودن به لیست مقایسه
                  </button>
                ))}
            </Flex>
          </Box>
          <Flex
            className="bg-background1 fixed right-0 bottom-0 left-0 z-[2] w-full"
            gap={1.6}
            flexWrap="nowrap"
            spacing={{ px: 'x11', py: 'x4' }}
          >
            {productInfo.inStock ? (
              <Button
                variant="contained"
                size="full width"
                onClick={() => {
                  handleClose();
                  setTimeout(() => {
                    router.push(`${OriginURL}/product/${productInfo.slug}/#${storesId}`);
                  }, 0);
                }}
                rightIcon={<Eye className="!fill-white" size="sm" />}
              >
                مشاهده فروشندگان
              </Button>
            ) : (
              <Button
                variant="contained"
                size="full width"
                onClick={() => {
                  handleClose();
                  setTimeout(() => {
                    router.push(`${OriginURL}/product/${productInfo.slug}/#${specificationsId}`);
                  }, 0);
                }}
              >
                مشاهده مشخصات کامل
              </Button>
            )}
            <Button onClick={handleClose} size={'medium'} variant="outline" colorScheme="secondary">
              بستن
            </Button>
          </Flex>
        </Sheet.Container>
        <Sheet.Backdrop />
      </Sheet>
      <PriceNotifyModal
        productId={productInfo.id}
        title={productInfo.title}
        image={productInfo.image}
        onClose={priceNotifyPopOverState.close}
        isPriceNotifyShow={priceNotifyPopOverState.isOpen}
        onSetNotify={() => postMutation.mutate()}
      />
    </Portal>
  );
};

export default ProductInfoSheet;
